import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { SectionPanelHeader } from '~/components/ui/SectionPanel';
import type { TallyBundleCount } from '~/components/common/tallyBundleCounts';
import {
  useSetCannedTallyOnBuyList,
  useRemoveTallyFromBuyList,
  useSetRandomTallyOnBuyList,
} from '~/api/buyList';
import { toastGraphQLError } from '~/utils';
import { BuyListTallyCard } from './BuyListTallyCard';
import type { BuyListDetailTally, CannedBuyListDetailTally } from './types';

type BuyListTalliesBlockProps = {
  buyListId: string;
  tallies: BuyListDetailTally[];
  canManage: boolean;
  onSavingChange?: (isSaving: boolean) => void;
};

const IGNORE_SAVING_CHANGE = () => {};

export function BuyListTalliesBlock({
  buyListId,
  tallies,
  canManage,
  onSavingChange = IGNORE_SAVING_CHANGE,
}: Readonly<BuyListTalliesBlockProps>) {
  const { t } = useTranslation('inventory');
  const [removingTallyId, setRemovingTallyId] = useState<string | null>(null);
  const { mutate: removeTallyFromBuyList } = useRemoveTallyFromBuyList();
  const { mutate: addCannedTally, isPending: isSavingCannedTally } =
    useSetCannedTallyOnBuyList();
  const { mutate: setRandomTally, isPending: isSettingRandomTally } =
    useSetRandomTallyOnBuyList();
  const isSaving = isSavingCannedTally || isSettingRandomTally;
  const isBusy = isSaving || removingTallyId !== null;
  useEffect(() => onSavingChange(isBusy), [isBusy, onSavingChange]);

  if (tallies.length === 0 && !canManage) return null;

  const removeTally = (tally: BuyListDetailTally) => {
    setRemovingTallyId(tally.id);
    removeTallyFromBuyList(
      { buyListId, buyListTallyId: tally.id },
      {
        onSuccess: () =>
          toast.success(t('inventory.buy_list.tallies.remove_success')),
        onError: toastGraphQLError,
        onSettled: () => setRemovingTallyId(null),
      }
    );
  };

  const saveCallbacks = (onSaved: () => void) => ({
    onSuccess: () => {
      toast.success(t('inventory.buy_list.tallies.save_success'));
      onSaved();
    },
    onError: toastGraphQLError,
  });

  const saveTallyCount = (
    tally: CannedBuyListDetailTally,
    tallyCount: number,
    onSaved: () => void
  ) => {
    addCannedTally(
      { buyListId, cannedTallyId: tally.cannedTally.id, tallyCount },
      saveCallbacks(onSaved)
    );
  };

  const saveRandomBundles = (
    tally: BuyListDetailTally,
    bundles: TallyBundleCount[],
    onSaved: () => void
  ) => {
    setRandomTally(
      { buyListId, platformProductId: tally.platformProduct.id, bundles },
      saveCallbacks(onSaved)
    );
  };

  return (
    <div className="border-t border-neut-border" data-testid="buy-list-tallies">
      <SectionPanelHeader
        className="border-b border-neut-border"
        title={t('inventory.buy_list.tallies.title')}
      />
      <div className="flex flex-col gap-3 p-4">
        {tallies.length === 0 ? (
          <p
            className="text-sm text-neut-sec"
            data-testid="buy-list-tallies-empty"
          >
            {t('inventory.buy_list.tallies.empty')}
          </p>
        ) : (
          tallies.map((tally) => (
            <BuyListTallyCard
              key={tally.id}
              tally={tally}
              canManage={canManage}
              onRemove={removeTally}
              isRemoving={removingTallyId === tally.id}
              onSaveTallyCount={saveTallyCount}
              onSaveRandomBundles={saveRandomBundles}
              isSaving={isSaving}
            />
          ))
        )}
      </div>
    </div>
  );
}
